import { KompisElement, property } from '@inter-ikea-kompis/web-component';
import { SheetAlignmentEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import type { ILocalizedInformation, IStoreAvailability, ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';

import { ModalHeader as SkapaModalHeader, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';

import BuyInStoreModalEnum from '../../enums/BuyInStoreModalEnum.js';
import type IBuyInStoreTranslations from '../../types/IBuyInStoreTranslations.js';
import BuyInStoreCard from '../buy-in-store-card/BuyInStoreCard.js';

/**
 * Opens a sheet with a QR code for the user to scan to complete their purchase of a Kiosk design.
 *
 * @event modalClose Modal close.
 * @event modalOpen Modal open.
 */

export default class BuyInStore extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IBuyInStoreTranslations | null = null;

	// A list of store availability objects.
	@property({ type: Array })
	public storeAvailabilities: IStoreAvailability[] | null = null;

	// Visible modal. Use ZipInModalEnum.
	@property({ type: String })
	public visibleModal: BuyInStoreModalEnum | null = null;

	// Where the sheet will open. The values are flipped in RTL, meaning "right" will open to the left and vice versa.
	@property({ type: String })
	public sheetAlignment: SheetAlignmentEnum = SheetAlignmentEnum.right;

	// Link used in the QR code to take the user to the summary page on their phone. This should link directly to the picking list of the summary page.
	@property({ type: String })
	public buyInStoreLink: string | null = null;

	private sheetElement: SkapaSheet | null = null;
	private cardElement: BuyInStoreCard | null = null;

	public override render(): RenderResult {
		return null;
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-sheet', SkapaSheet);
		this.customElements.define('skapa-modal-header', SkapaModalHeader);
		this.customElements.define('kompis-buy-in-store-card', BuyInStoreCard);

		super.connectedCallback();
	}

	protected override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.closeSheet();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('visibleModal')) {
			if (this.visibleModal === BuyInStoreModalEnum.buyInStore) {
				this.openSheet();
			} else {
				this.closeSheet();
			}
		}

		if (this.cardElement) {
			this.setCardProperties(this.cardElement);
		}

		return true;
	}

	private getTranslations(): IBuyInStoreTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		if (!translations) {
			throw new Error(
				'Translations must be defined either through the "translations" or "localizedInformation" prop.'
			);
		}

		return translations;
	}

	private closeSheet(): void {
		if (this.sheetElement) {
			this.sheetElement.open = false;
			this.sheetElement = null;
			this.cardElement = null;
		}
	}

	private openSheet(): void {
		if (this.sheetElement) {
			return;
		}

		this.requestUpdate();

		const sheetElement: SkapaSheet = this.createSheetElement();
		sheetElement.addEventListener('closed', () => {
			sheetElement.parentElement?.removeChild(sheetElement);
		});

		this.sheetElement = sheetElement;
		this.cardElement = sheetElement.querySelector(
			this.customElements.getInternalName('kompis-buy-in-store-card')
		);

		const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
		parent.appendChild(sheetElement);

		sheetElement.open = true;

		this.dispatchEvent(
			new CustomEvent('modalOpen', {
				bubbles: true,
				detail: {
					visibleModal: BuyInStoreModalEnum.buyInStore
				}
			})
		);
	}

	private createSheetElement(): SkapaSheet {
		const sheetElement = this.createSheet();
		const sheetHeaderElement = this.createSheetHeader();
		const card = this.createCard();

		sheetElement.appendChild(sheetHeaderElement);
		sheetElement.appendChild(card);

		return sheetElement;
	}

	private createSheet(): SkapaSheet {
		const sheetElement = this.createInternalElement('skapa-sheet');
		sheetElement.setAttribute('aria-label', this.getTranslations().continueOnMobileHeading);
		sheetElement.size = 'small';
		sheetElement.style.zIndex = '500';
		sheetElement.fullHeight = true;
		sheetElement.alignToStart = this.sheetAlignment === SheetAlignmentEnum.left;
		sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
		sheetElement.addEventListener('closerequest', () => {
			if (this.sheetElement) {
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: {
							visibleModal: null
						}
					})
				);
			}
		});

		return sheetElement;
	}

	private createSheetHeader(): SkapaModalHeader {
		const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
		sheetHeaderElement.slot = 'header';
		const headerSpan = document.createElement('span');
		headerSpan.slot = 'closebutton-label';
		headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;

		sheetHeaderElement.appendChild(headerSpan);

		return sheetHeaderElement;
	}

	private createCard(): BuyInStoreCard {
		const card = this.createInternalElement('kompis-buy-in-store-card');
		this.setCardProperties(card);

		return card;
	}

	private setCardProperties(cardElement: BuyInStoreCard): void {
		cardElement.localizedInformation = this.localizedInformation;
		cardElement.translations = this.translations;
		cardElement.storeAvailabilities = this.storeAvailabilities;
		cardElement.buyInStoreLink = this.buyInStoreLink;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-sheet': SkapaSheet;
		'skapa-modal-header': SkapaModalHeader;
		'kompis-buy-in-store': BuyInStore;
		'kompis-buy-in-store-card': BuyInStoreCard;
	}
}
